<template>
  <div :class="ns.b()" :style="{ height: designerHeight }">
    <FcDesigner
      ref="designerRef"
      :height="designerHeight"
      :mask="mask"
      :config="config ?? {}"
      @create="emitSchema"
      @delete="emitSchema"
      @drag="emitSchema"
      @copy="emitSchema"
      @clear="emitSchema"
      @paste-rule="emitSchema"
      @save="emitSchema"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, nextTick, onMounted, ref, watch } from 'vue'
import FcDesignerSource, {
  designerForm,
  formCreate,
} from './designer'
import { bindZorynFormCreate } from '../../form-create/src/bind-components'
import { useNamespace } from '@zoryn/hooks'
import { formDesignerProps } from './form-designer'
import { installDesignerHost } from './install-host'

import type { Plugin } from 'vue'
import type {
  FormCreateOption,
  FormRule,
  FormSchema,
} from '@zoryn/components/form-create'

defineOptions({
  name: 'ZFormDesigner',
})

const FcDesigner =
  (FcDesignerSource as { default?: typeof FcDesignerSource }).default ??
  FcDesignerSource

const props = defineProps(formDesignerProps)
const emit = defineEmits<{
  'update:modelValue': [value: FormSchema]
  'update:rule': [value: FormRule[]]
  'update:option': [value: FormCreateOption]
}>()

const ns = useNamespace('form-designer')
const designerRef = ref<{
  getRule: () => FormRule[]
  setRule: (rule: string | FormRule[]) => void
  getOption: () => FormCreateOption
  setOption: (opt: FormCreateOption) => void
  addComponent: (rule: unknown) => void
  clearDragRule: () => void
} | null>(null)

const instance = getCurrentInstance()
if (instance?.appContext.app) {
  installDesignerHost(
    instance.appContext.app,
    FcDesigner as typeof FcDesigner & Plugin
  )
}
bindZorynFormCreate(formCreate)
bindZorynFormCreate(designerForm)

const designerHeight = computed(() =>
  typeof props.height === 'number' ? `${props.height}px` : props.height
)

let applying = false

function currentSchema(): FormSchema {
  return {
    rule: designerRef.value?.getRule?.() ?? [],
    option: designerRef.value?.getOption?.() ?? {},
  }
}

function emitSchema() {
  if (applying) return
  const schema = currentSchema()
  emit('update:modelValue', schema)
  emit('update:rule', schema.rule)
  emit('update:option', schema.option ?? {})
}

function applySchema(
  schema?: FormSchema,
  rules?: FormRule[],
  option?: FormCreateOption
) {
  const designer = designerRef.value
  if (!designer) return
  applying = true
  const nextRules = schema?.rule ?? rules
  const nextOption = schema?.option ?? option
  if (nextRules) designer.setRule(nextRules)
  if (nextOption) designer.setOption(nextOption)
  queueMicrotask(() => {
    applying = false
  })
}

watch(
  () => props.modelValue,
  (schema) => {
    if (applying || !schema) return
    applySchema(schema)
  },
  { deep: true }
)

watch(
  () => props.rule,
  (rules) => {
    if (applying || !rules || props.modelValue) return
    applySchema(undefined, rules)
  }
)

watch(
  () => props.option,
  (option) => {
    if (applying || !option || props.modelValue) return
    applySchema(undefined, undefined, option)
  }
)

onMounted(async () => {
  await nextTick()
  applySchema(props.modelValue, props.rule, props.option)
})

defineExpose({
  getRule: () => designerRef.value?.getRule?.() ?? [],
  getOption: () => designerRef.value?.getOption?.() ?? {},
  setRule: (rules: FormRule[]) => {
    designerRef.value?.setRule(rules)
    emitSchema()
  },
  setOption: (option: FormCreateOption) => {
    designerRef.value?.setOption(option)
    emitSchema()
  },
  addComponent: (rule: unknown) => designerRef.value?.addComponent(rule),
  clearDragRule: () => {
    designerRef.value?.clearDragRule()
    emitSchema()
  },
})
</script>
